/**
 * 코드값 편집.
 *
 * 줄마다 코드·표기·사용 여부 세 칸이다. 예전처럼 textarea 한 칸으로 두면 코드를
 * 표현할 수 없다 — 표기를 고칠 때마다 이미 저장된 고객이 가리키던 값이 바뀐다.
 *
 * **내장 코드는 지우지 못한다**(사용 안 함으로만). 그 코드로 저장된 고객이 이미
 * 있고, 목록에서 사라지면 그 고객의 편집이 「목록에 없다」 로 막힌다.
 * 코드는 한 번 쓰이면 못 바꾸므로 글로만 보이고, 새 줄의 코드는 화면이 만든다.
 */

import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { useMemo, useState, useTransition } from "react";
import { toast } from "sonner";

import SaveBar from "@/components/save-bar";
import SectionCard from "@buildpy/ui/section-card";
import { Button } from "@buildpy/ui/button";
import { Checkbox } from "@buildpy/ui/checkbox";
import { Input } from "@buildpy/ui/input";
import { Label } from "@buildpy/ui/label";
import { settleRedirect } from "@/lib/redirect-error";
import { cn } from "@/lib/utils";
import { codeSaveFailure } from "@buildpy/core/modules/org/code-save-message";
import type { CodeEntry, CodeKey } from "@buildpy/core/modules/org/codes";
import { saveCodes } from "@/api/actions/org";

export interface CodeGroupView {
  key: CodeKey;
  label: string;
  hint: string;
  entries: CodeEntry[];
  /** 지울 수 없는 내장 코드들. */
  builtin: string[];
}

interface Props {
  groups: CodeGroupView[];
  canEdit: boolean;
}

const move = <T,>(rows: T[], index: number, delta: number): T[] => {
  const next = index + delta;
  if (next < 0 || next >= rows.length) return rows;
  const copy = rows.slice();
  [copy[index], copy[next]] = [copy[next], copy[index]];
  return copy;
};

// 새 줄의 코드는 사람이 치지 않는다 — 영문·숫자·_ 만(액션의 코드 규칙), 겹치지 않게 시각+난수.
const newCode = (): string =>
  `c_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

const SettingsCodesEditor = ({ groups, canEdit }: Props) => {
  const [pending, startTransition] = useTransition();
  const initial = useMemo(
    () => Object.fromEntries(groups.map((group) => [group.key, group.entries])),
    [groups]
  );
  const [rows, setRows] = useState<Record<string, CodeEntry[]>>(initial);
  // 마지막으로 저장된 모양 — 묶음마다 이것과 달라야 저장 대상이다.
  const [saved, setSaved] = useState<Record<string, CodeEntry[]>>(initial);
  // 아직 저장 안 한 새 줄의 코드 — 화면에는 코드 대신 「새 항목」 으로 보인다.
  const [fresh, setFresh] = useState<string[]>([]);

  const patch = (key: string, next: CodeEntry[]) =>
    setRows((prev) => ({ ...prev, [key]: next }));

  const dirtyKeys: CodeKey[] = groups
    .map((group) => group.key)
    .filter((key) => JSON.stringify(rows[key]) !== JSON.stringify(saved[key]));

  // 고친 묶음만 차례로 보낸다 — 하나가 실패하면 거기서 멈추고(어느 묶음·어느 줄인지 알린다), 성공한 묶음은 저장된 모양을 갱신한다. 다 되면 알림은 한 번.
  const onSave = () => {
    startTransition(async () => {
      for (const key of dirtyKeys) {
        const list = rows[key];
        const group = groups.find((item) => item.key === key)?.label ?? key;
        try {
          const result = await saveCodes(key, list);
          if (!result.ok) {
            toast.error(codeSaveFailure(group, list, fresh, result));
            return;
          }
          setSaved((prev) => ({ ...prev, [key]: list }));
          setFresh((prev) => prev.filter((code) => !list.some((row) => row.code === code)));
        } catch (caught) {
          // 세션이 끊기면(401) settleRedirect 가 세션을 지우고 /login 으로 옮긴다 — 토스트로 삼키지 않는다.
          if (settleRedirect(caught)) return;
          toast.error(`${group} — 저장하지 못했습니다. 잠시 후 다시 시도하세요.`);
          return;
        }
      }
      toast.success("저장했습니다");
    });
  };

  return (
    <div className="flex flex-col gap-y-4">
      {!canEdit && (
        <p className="text-sm text-muted-foreground">
          대표만 코드값을 바꿀 수 있습니다.
        </p>
      )}

      <p className="text-sm text-muted-foreground">
        고객 등록·편집의 선택지입니다. 표기를 바꾸면 이미 저장된 고객도 새 표기로
        보입니다 — 값 자체(코드)는 그대로라서 그렇습니다.
      </p>

      <div className="grid gap-4 xl:grid-cols-2">
        {groups.map((group) => {
          const list = rows[group.key] ?? [];
          return (
            <SectionCard
              key={group.key}
              className="flex flex-col gap-y-2"
              padding="sm">
              <div className="flex flex-wrap items-baseline justify-between gap-x-2">
                <Label>{group.label}</Label>
                <span className="text-xs text-muted-foreground">
                  {group.hint}
                </span>
              </div>

              <div className="flex flex-col divide-y divide-border rounded-md border border-border">
                {/* md 이상은 한 줄 표(코드 6.55rem·표기 5.8rem 이상(text-xs 12px 기준) = 내장 값이 안 잘리는 폭 · 끝 칸은 아이콘 3개가 드는 5rem · 머리줄과 같은 트랙), md 미만은 머리줄을 숨기고 코드|표기 / 사용·순서 두 줄 */}
                <div className="hidden items-center gap-x-2 bg-muted/40 px-2 py-1.5 text-xs text-muted-foreground md:grid md:grid-cols-[minmax(6.55rem,7rem)_minmax(5.8rem,1fr)_2.5rem_5rem]">
                  <span>코드</span>
                  <span>표기</span>
                  <span className="text-center">사용</span>
                  <span className="text-right">순서</span>
                </div>

                {list.map((row, index) => {
                  const isBuiltin = group.builtin.includes(row.code);
                  return (
                    <div
                      key={`${group.key}-${index}`}
                      className={cn(
                        "grid grid-cols-2 items-center gap-x-2 gap-y-1.5 px-2 py-1.5 md:grid-cols-[minmax(6.55rem,7rem)_minmax(5.8rem,1fr)_2.5rem_5rem]",
                        !row.active && "bg-muted/30"
                      )}>
                      {/* 코드는 저장된 고객이 가리키는 값이라 고치지 못한다 — 입력칸이 아니라 글로 보인다 */}
                      <span
                        className="truncate font-mono text-xs text-muted-foreground"
                        data-testid="code-text"
                        title={row.code}>
                        {fresh.includes(row.code) ? "새 항목" : row.code}
                      </span>
                      <Input
                        aria-label={`${group.label} 표기`}
                        inputSize="xs"
                        name={`${group.key}-label-${index}`}
                        disabled={!canEdit || pending}
                        onChange={(event) =>
                          patch(
                            group.key,
                            list.map((item, at) =>
                              at === index
                                ? { ...item, label: event.target.value }
                                : item
                            )
                          )
                        }
                        placeholder="화면에 보이는 말"
                        value={row.label}
                      />
                      <div className="col-span-2 flex items-center justify-between gap-x-2 md:contents">
                        <div className="flex items-center justify-center gap-x-1.5">
                          <Checkbox
                            aria-label={`${row.label} 사용`}
                            id={`${group.key}-active-${index}`}
                            checked={row.active}
                            disabled={!canEdit || pending}
                            onCheckedChange={(value) =>
                              patch(
                                group.key,
                                list.map((item, at) =>
                                  at === index
                                    ? { ...item, active: value === true }
                                    : item
                                )
                              )
                            }
                          />
                          {/* 두 줄일 때 「사용」 글자를 눌러도 체크박스가 바뀐다 — 이름은 aria-label 이 정한다 */}
                          <Label
                            className="md:hidden"
                            variant="option"
                            htmlFor={`${group.key}-active-${index}`}>
                            사용
                          </Label>
                        </div>
                        <div className="flex items-center justify-end gap-x-2 md:gap-x-0.5">
                          <Button
                            aria-label="위로"
                            disabled={!canEdit || pending || index === 0}
                            onClick={() => patch(group.key, move(list, index, -1))}
                            size="icon-xs"
                            type="button"
                            variant="ghost-muted">
                            <ArrowUp className="size-4" />
                          </Button>
                          <Button
                            aria-label="아래로"
                            disabled={
                              !canEdit || pending || index === list.length - 1
                            }
                            onClick={() => patch(group.key, move(list, index, 1))}
                            size="icon-xs"
                            type="button"
                            variant="ghost-muted">
                            <ArrowDown className="size-4" />
                          </Button>
                          {/* disabled Button 은 pointer-events-none 이라 기본 코드 안내 툴팁은 감싼 span 이 띄운다 */}
                          <span
                            className="inline-flex shrink-0"
                            title={
                              isBuiltin
                                ? "기본 코드는 사용 안 함으로만 둘 수 있습니다"
                                : undefined
                            }>
                            <Button
                              aria-label="삭제"
                              disabled={!canEdit || pending || isBuiltin}
                              onClick={() =>
                                patch(
                                  group.key,
                                  list.filter((_, at) => at !== index)
                                )
                              }
                              title={isBuiltin ? undefined : "삭제"}
                              size="icon-xs"
                              type="button"
                              variant="ghost-destructive">
                              <Trash2 className="size-4" />
                            </Button>
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex items-center justify-between gap-x-2">
                <Button
                  disabled={!canEdit || pending}
                  onClick={() => {
                    const code = newCode();
                    setFresh((prev) => [...prev, code]);
                    patch(group.key, [...list, { code, label: "", active: true }]);
                  }}
                  size="sm"
                  type="button"
                  variant="outline">
                  <Plus className="size-3.5" />줄 추가
                </Button>
              </div>
            </SectionCard>
          );
        })}
      </div>

      {canEdit && (
        <SaveBar dirty={dirtyKeys.length > 0} onSave={onSave} pending={pending} />
      )}
    </div>
  );
};

export default SettingsCodesEditor;
